import styles from './MissingLocaleMessageDialogBox.module.css';

/**
 * Confirmation dialog shown when the user attempts to send/create a document
 * but one or more locales in the recipient list have an empty message.
 *
 * Only two actions keep the user on the current page:
 *  - the close icon in the header
 *  - the "No" button in the footer
 *
 * The "Yes" button delegates back to the parent to continue the send/create
 * flow.
 */
const MissingLocaleMessageDialogBox = ({
  show,
  localeCodes = [],
  languageNames = {},
  onClose,
  onNo,
  onYes,
}) => {
  if (!show) return null;

  const labels = (localeCodes || [])
    .map((code) => languageNames[code] || code)
    .filter(Boolean);

  let content;
  if (labels.length === 0) {
    content = 'Document message is missing for one or more languages. Would you like to proceed with that?';
  } else if (labels.length === 1) {
    content = `Document message missing for ${labels[0]} language. Would you like to proceed with that?`;
  } else {
    content = `Document message missing for ${labels.join(', ')} language(s). Would you like to proceed with that?`;
  }

  const handleClose = () => {
    if (typeof onClose === 'function') onClose();
  };

  const handleNo = () => {
    if (typeof onNo === 'function') onNo();
  };

  const handleYes = () => {
    if (typeof onYes === 'function') onYes();
  };

  return (
    <div
      className={styles.dialogOverlay}
      role="presentation"
    >
      <div
        className={styles.dialogBox}
        role="dialog"
        aria-modal="true"
        aria-labelledby="locale-message-dialog-title"
      >
        <div className={styles.dialogHeader}>
          <h3 id="locale-message-dialog-title" className={styles.dialogTitle}>
            Confirmation
          </h3>
          <div
            role="button"
            tabIndex={0}
            className={styles.closeButton}
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClose();
              }
            }}
            aria-label="Close dialog"
          >
            <span
              className={`${styles.iconBase} ${styles.closeIcon} sfIconBase sfCloseIcon`}
            ></span>
          </div>
        </div>

        <div className={styles.dialogContent}>{content}</div>

        <div className={styles.dialogFooter}>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.footerButtonNo} secondary-button`}
            onClick={handleNo}
          >
            No
          </button>
          <button
            type="button"
            className={`${styles.footerButton} primary-button`}
            onClick={handleYes}
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissingLocaleMessageDialogBox;
